import { Head } from "@inertiajs/react"
import { TrendingUp, Target, BarChart3, Activity } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { InfoTooltip } from "@/components/info-tooltip"
import { AppLayout } from "@/layouts/app-layout"
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts"

interface Portfolio {
  id: number
  name: string
  total_value: number
  total_cost_basis: number
  total_return: number
  total_return_percentage: number
  portfolio_positions: {
    id: number
    symbol: string
    shares: number
    current_price: number
    average_cost: number
  }[]
}

interface PerformanceMetrics {
  total_cost_basis: number
  total_current_value: number
  total_return: number
  total_return_percent: number
  total_dividends: number
  total_dividend_yield: number
  position_count: number
  positions: {
    symbol: string
    shares: number
    cost_basis: number
    current_value: number
    unrealized_gain_loss: number
    unrealized_gain_loss_percent: number
    dividends: number
    dividend_yield: number
    current_price: number
    weight: number
  }[]
  last_updated: string
}

interface RiskMetrics {
  concentration_risk: string | null | undefined
  max_position_weight: number | null | undefined
  diversification_score: string | null | undefined
  position_count: number | null | undefined
  total_value: number
}

interface FireAnalysis {
  current_value: number
  fire_number: number
  monthly_savings: number
  years_to_fire: number
  success_probability: number
  simulation: {
    simulations: number
    years: number
    percentiles: {
      p10: number
      p25: number
      p50: number
      p75: number
      p90: number
    }
    success_rate_4_percent_rule: number
    success_rate_3_percent_rule: number
  }
  recommendations: string[]
}

interface PortfoliosAnalyticsProps {
  portfolio: Portfolio
  performance_metrics: PerformanceMetrics
  risk_metrics: RiskMetrics
  fire_analysis: FireAnalysis
}

export default function PortfoliosAnalytics({
  portfolio,
  performance_metrics,
  risk_metrics,
  fire_analysis,
}: PortfoliosAnalyticsProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatPercentage = (percentage: number | null | undefined) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) {
      return "0.00%"
    }
    // Additional safety check in case percentage is not a number
    const numPercentage =
      typeof percentage === "number"
        ? percentage
        : parseFloat(String(percentage))
    if (isNaN(numPercentage)) {
      return "0.00%"
    }
    return `${numPercentage >= 0 ? "+" : ""}${numPercentage.toFixed(2)}%`
  }

  const getReturnColor = (return_percentage: number | null | undefined) => {
    if (
      return_percentage === null ||
      return_percentage === undefined ||
      isNaN(return_percentage)
    ) {
      return "text-gray-600"
    }
    if (return_percentage > 0) return "text-green-600"
    if (return_percentage < 0) return "text-red-600"
    return "text-gray-600"
  }

  const getRiskBadge = (risk: string | null | undefined) => {
    if (!risk) {
      return (
        <Badge className="border-gray-300 bg-gray-100 text-gray-800">
          Unknown Risk
        </Badge>
      )
    }

    const colorClass =
      risk.toLowerCase() === "high"
        ? "bg-red-100 text-red-800 border-red-300"
        : risk.toLowerCase() === "medium"
          ? "bg-yellow-100 text-yellow-800 border-yellow-300"
          : "bg-green-100 text-green-800 border-green-300"

    return <Badge className={colorClass}>{risk} Risk</Badge>
  }

  // Prepare chart data
  const portfolioAllocationData = performance_metrics.positions.map((pos) => ({
    name: pos.symbol,
    value: pos.current_value,
    weight: pos.weight,
  }))

  const performanceData = [
    { name: "Cost Basis", value: performance_metrics.total_cost_basis },
    { name: "Current Value", value: performance_metrics.total_current_value },
    { name: "Total Return", value: performance_metrics.total_return },
  ]

  const monteCarloData = [
    { percentile: "10th", value: fire_analysis.simulation.percentiles.p10 },
    { percentile: "25th", value: fire_analysis.simulation.percentiles.p25 },
    { percentile: "50th", value: fire_analysis.simulation.percentiles.p50 },
    { percentile: "75th", value: fire_analysis.simulation.percentiles.p75 },
    { percentile: "90th", value: fire_analysis.simulation.percentiles.p90 },
  ]

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"]

  return (
    <AppLayout>
      <Head title={`${portfolio.name} - Analytics`} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {portfolio.name} Analytics
            </h1>
            <p className="text-muted-foreground">
              Advanced portfolio analysis and FIRE projections
            </p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Return
              </CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(performance_metrics.total_return)}
              </div>
              <p
                className={`text-xs ${getReturnColor(performance_metrics?.total_return_percent ?? null)}`}
              >
                {formatPercentage(
                  performance_metrics?.total_return_percent ?? null,
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
                Dividend Yield
                <InfoTooltip text="Dividends received over the last 12 months as a percentage of your portfolio's current value." />
              </CardTitle>
              <Target className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatPercentage(
                  performance_metrics?.total_dividend_yield ?? null,
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatCurrency(performance_metrics.total_dividends)} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
                FIRE Success
                <InfoTooltip text="The odds this portfolio can sustain your planned withdrawals through retirement, based on 1,000 simulated market scenarios (Monte Carlo simulation)." />
              </CardTitle>
              <BarChart3 className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {fire_analysis?.success_probability &&
                !isNaN(fire_analysis.success_probability)
                  ? `${fire_analysis.success_probability.toFixed(1)}%`
                  : "0.0%"}
              </div>
              <p className="text-muted-foreground text-xs">
                {fire_analysis.years_to_fire} years to FIRE
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
                Risk Level
                <InfoTooltip text="How concentrated your portfolio is in a small number of positions. Fewer, larger positions mean higher risk if one underperforms." />
              </CardTitle>
              <Activity className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {getRiskBadge(risk_metrics?.concentration_risk)}
              </div>
              <p className="text-muted-foreground text-xs">
                {risk_metrics?.position_count ?? 0} positions
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Tabs */}
        <Tabs defaultValue="performance" className="space-y-4">
          <TabsList>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="allocation">Allocation</TabsTrigger>
            <TabsTrigger value="fire">FIRE Analysis</TabsTrigger>
            <TabsTrigger value="risk">Risk Analysis</TabsTrigger>
          </TabsList>

          <TabsContent value="performance" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Portfolio Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis
                        tickFormatter={(value: number) =>
                          `$${!isNaN(value) ? (value / 1000).toFixed(0) : "0"}k`
                        }
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Performers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {performance_metrics.positions
                      .sort(
                        (a, b) =>
                          b.unrealized_gain_loss_percent -
                          a.unrealized_gain_loss_percent,
                      )
                      .slice(0, 5)
                      .map((position) => (
                        <div
                          key={position.symbol}
                          className="flex items-center justify-between"
                        >
                          <div>
                            <div className="font-medium">{position.symbol}</div>
                            <div className="text-muted-foreground text-sm">
                              {position.shares} shares
                            </div>
                          </div>
                          <div className="text-right">
                            <div
                              className={`font-medium ${getReturnColor(position?.unrealized_gain_loss_percent ?? null)}`}
                            >
                              {formatPercentage(
                                position?.unrealized_gain_loss_percent ?? null,
                              )}
                            </div>
                            <div className="text-muted-foreground text-sm">
                              {formatCurrency(position.unrealized_gain_loss)}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="allocation" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Portfolio Allocation</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <RechartsPieChart>
                    <Pie
                      data={portfolioAllocationData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (props: any) => {
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, react/prop-types
                          const { name, weight } = props
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, react/prop-types
                          return `${name} (${!isNaN(weight) ? weight.toFixed(1) : "0.0"}%)`
                        }
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {portfolioAllocationData.map((_entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fire" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Monte Carlo Simulation</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monteCarloData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="percentile" />
                      <YAxis
                        tickFormatter={(value: number) =>
                          `$${!isNaN(value) ? (value / 1000).toFixed(0) : "0"}k`
                        }
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Bar dataKey="value" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>FIRE Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Current Value</span>
                      <span className="font-medium">
                        {formatCurrency(fire_analysis.current_value)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>FIRE Number</span>
                      <span className="font-medium">
                        {formatCurrency(fire_analysis.fire_number)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Monthly Savings</span>
                      <span className="font-medium">
                        {formatCurrency(fire_analysis.monthly_savings)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Years to FIRE</span>
                      <span className="font-medium">
                        {fire_analysis.years_to_fire}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Success Probability</span>
                      <span className="font-medium">
                        {fire_analysis?.success_probability &&
                        !isNaN(fire_analysis.success_probability)
                          ? `${fire_analysis.success_probability.toFixed(1)}%`
                          : "0.0%"}
                      </span>
                    </div>
                    <Progress
                      value={fire_analysis.success_probability}
                      className="h-2"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      Withdrawal Success Rates
                      <InfoTooltip text="Across 1,000 simulated 30-year retirements, the percentage that didn't run out of money when withdrawing this rate each year." />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>4% Rule (30 years)</span>
                      <span className="font-medium">
                        {fire_analysis?.simulation
                          ?.success_rate_4_percent_rule &&
                        !isNaN(
                          fire_analysis.simulation.success_rate_4_percent_rule,
                        )
                          ? `${fire_analysis.simulation.success_rate_4_percent_rule.toFixed(1)}%`
                          : "0.0%"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>3% Rule (30 years)</span>
                      <span className="font-medium">
                        {fire_analysis?.simulation
                          ?.success_rate_3_percent_rule &&
                        !isNaN(
                          fire_analysis.simulation.success_rate_3_percent_rule,
                        )
                          ? `${fire_analysis.simulation.success_rate_3_percent_rule.toFixed(1)}%`
                          : "0.0%"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {fire_analysis.recommendations.map(
                    (recommendation, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                        <p className="text-sm">{recommendation}</p>
                      </div>
                    ),
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="risk" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Risk Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="flex items-center gap-1.5">
                        Concentration Risk
                        <InfoTooltip text="How much of your portfolio's value sits in your single largest position. A high concentration means one stock can swing your whole portfolio." />
                      </span>
                      <span className="font-medium">
                        {getRiskBadge(risk_metrics?.concentration_risk)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Largest Position</span>
                      <span className="font-medium">
                        {risk_metrics?.max_position_weight != null &&
                        !isNaN(risk_metrics.max_position_weight) &&
                        typeof risk_metrics.max_position_weight === "number"
                          ? `${risk_metrics.max_position_weight.toFixed(1)}%`
                          : "0.0%"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="flex items-center gap-1.5">
                        Diversification
                        <InfoTooltip text="A rough score for how spread out your holdings are across positions. More positions, more evenly weighted, means a better score." />
                      </span>
                      <span className="font-medium">
                        {getRiskBadge(risk_metrics?.diversification_score)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Position Count</span>
                      <span className="font-medium">
                        {risk_metrics?.position_count ?? 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Position Weights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {performance_metrics.positions
                      .sort((a, b) => b.weight - a.weight)
                      .map((position) => (
                        <div key={position.symbol} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{position.symbol}</span>
                            <span className="font-medium">
                              {!isNaN(position.weight)
                                ? `${position.weight.toFixed(1)}%`
                                : "0.0%"}
                            </span>
                          </div>
                          <Progress value={position.weight} className="h-2" />
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  )
}
